import React, { useState, useEffect, useCallback } from 'react';
import { stockApi } from '../services/stockApi';
import '../styles/StockScanner.css'; // We'll create this CSS file

const StockScanner = () => {
  // State management
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStock, setSelectedStock] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  
  // Filter state
  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 10000,
    minMarketCap: 1000000000, // $1B
    maxMarketCap: 1000000000000, // $1T
    minPE: 0,
    maxPE: 100,
    minDividend: 0,
    maxDividend: 20,
    minRSI: 0,
    maxRSI: 100,
    minMomentum: -50,
    maxMomentum: 100,
    sortBy: 'score',
    sortOrder: 'desc'
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Fetch sectors on component mount
  useEffect(() => {
    fetchSectors();
  }, []);

  // Fetch stocks when sector changes
  useEffect(() => {
    if (selectedSector) {
      fetchStocksBySector();
    }
  }, [selectedSector]);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, stocks]);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await stockApi.getSectors();
      console.log('Sectors response:', response.sectors);
      setSectors(response.sectors || []);
      
      // Select first sector by default
      if (response.sectors?.length > 0) {
        setSelectedSector(response.sectors[0].key);
      }
    } catch (err) {
        console.error('Error fetching sectors:', err);
      setError('Failed to load sectors. Using default sectors.');
      // Fallback sectors
      setSectors([
        { key: 'TECHNOLOGY', name: 'Information Technology' },
        { key: 'HEALTHCARE', name: 'Health Care' },
        { key: 'FINANCIALS', name: 'Financials' },
        { key: 'CONSUMER_DISCRETIONARY', name: 'Consumer Discretionary' },
        { key: 'CONSUMER_STAPLES', name: 'Consumer Staples' },
        { key: 'INDUSTRIALS', name: 'Industrials' },
        { key: 'ENERGY', name: 'Energy' },
        { key: 'UTILITIES', name: 'Utilities' },
        { key: 'REAL_ESTATE', name: 'Real Estate' },
        { key: 'MATERIALS', name: 'Materials' },
        { key: 'COMMUNICATION_SERVICES', name: 'Communication Services' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStocksBySector = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await stockApi.getTopStocksBySector(selectedSector, 10);
      console.log('Selected Stocks response:', response);
      setStocks(response.stocks || []);
      setFilteredStocks(response.stocks || []);
    } catch (err) {
      setError('Failed to load stocks. Showing sample data.');
      // Mock data for demonstration
      const mockStocks = generateMockStocks();
      setStocks(mockStocks);
      setFilteredStocks(mockStocks);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!stocks.length) return;

    let filtered = [...stocks];

    // Apply price filter
    filtered = filtered.filter(stock => 
      stock.price >= filters.minPrice && 
      stock.price <= filters.maxPrice
    );

    // Apply market cap filter
    filtered = filtered.filter(stock => 
      stock.market_cap >= filters.minMarketCap && 
      stock.market_cap <= filters.maxMarketCap
    );

    // Apply P/E filter
    filtered = filtered.filter(stock => {
      const pe = stock.pe_ratio || 0;
      return pe >= filters.minPE && pe <= filters.maxPE;
    });

    // Apply dividend filter
    filtered = filtered.filter(stock => 
      stock.dividend_yield >= filters.minDividend && 
      stock.dividend_yield <= filters.maxDividend
    );

    // Apply RSI filter
    filtered = filtered.filter(stock => 
      stock.rsi >= filters.minRSI && 
      stock.rsi <= filters.maxRSI
    );

    // Apply momentum filter
    filtered = filtered.filter(stock => 
      stock.momentum >= filters.minMomentum && 
      stock.momentum <= filters.maxMomentum
    );

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[filters.sortBy] || 0;
      const bValue = b[filters.sortBy] || 0;
      
      if (filters.sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    setFilteredStocks(filtered);
    setCurrentPage(1); // Reset to first page
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  const resetFilters = () => {
    setFilters({
      minPrice: 0,
      maxPrice: 10000,
      minMarketCap: 1000000000,
      maxMarketCap: 1000000000000,
      minPE: 0,
      maxPE: 100,
      minDividend: 0,
      maxDividend: 20,
      minRSI: 0,
      maxRSI: 100,
      minMomentum: -50,
      maxMomentum: 100,
      sortBy: 'score',
      sortOrder: 'desc'
    });
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStocks = filteredStocks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStocks.length / itemsPerPage);

  // Utility functions
  const formatMarketCap = (value) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toFixed(2)}`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981'; // Green
    if (score >= 60) return '#f59e0b'; // Yellow
    if (score >= 40) return '#ef4444'; // Red
    return '#6b7280'; // Gray
  };

  const getRSIColor = (rsi) => {
    if (rsi > 70) return '#ef4444'; // Overbought - Red
    if (rsi < 30) return '#10b981'; // Oversold - Green
    return '#6b7280'; // Neutral
  };

  const getMomentumColor = (momentum) => {
    if (momentum > 0) return '#10b981'; // Positive - Green
    if (momentum < 0) return '#ef4444'; // Negative - Red
    return '#6b7280'; // Neutral
  };

  const handleStockSelect = (stock) => {
    setSelectedStock(selectedStock?.symbol === stock.symbol ? null : stock);
  };

  // Generate mock data for demonstration
  const generateMockStocks = () => {
    const symbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'TSLA', 'NVDA', 'JPM', 'JNJ', 'V', 'WMT', 'PG', 'XOM', 'HD', 'DIS'];
    const sectors = ['Technology', 'Healthcare', 'Financials', 'Consumer', 'Energy', 'Industrials'];
    
    return symbols.map((symbol, index) => ({
      symbol,
      name: `${symbol} Company`,
      sector: sectors[index % sectors.length],
      price: Math.random() * 500 + 50,
      market_cap: Math.random() * 500e9 + 50e9,
      pe_ratio: Math.random() * 40 + 10,
      dividend_yield: Math.random() * 5,
      beta: Math.random() * 2 + 0.5,
      rsi: Math.random() * 100,
      momentum: Math.random() * 40 - 10,
      volume: Math.random() * 10e6 + 1e6,
      score: Math.random() * 40 + 60,
      sma_50: Math.random() * 500 + 50,
      sma_200: Math.random() * 500 + 50,
      volatility: Math.random() * 30 + 10
    }));
  };

  if (loading && !stocks.length) {
    return (
      <div className="scanner-loading">
        <div className="spinner"></div>
        <p>Loading stock data...</p>
      </div>
    );
  }

  return (
    <div className="stock-scanner">
      {/* Header */}
      <div className="scanner-header">
        <div className="header-left">
          <h1 className="scanner-title">Stock Scanner</h1>
          <p className="scanner-subtitle">Scan and filter stocks by sector and metrics</p>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <i className="fas fa-th-large"></i> Grid
            </button>
            <button 
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <i className="fas fa-table"></i> Table
            </button>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="fas fa-filter"></i> 
            {showFilters ? ' Hide Filters' : ' Show Filters'}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="scanner-main">
        {/* Left Sidebar - Sector Selection & Filters */}
        <div className="scanner-sidebar">
          {/* Sector Selection */}
          <div className="sidebar-section">
            <h3 className="section-title">
              <i className="fas fa-chart-pie"></i> Select Sector
            </h3>
            <div className="sector-list">
              {sectors.map(sector => (
                <button
                  key={sector.key}
                  className={`sector-btn ${selectedSector === sector.key ? 'active' : ''}`}
                  onClick={() => setSelectedSector(sector.key)}
                >
                  <span className="sector-name">{sector.name}</span>
                  <span className="sector-count">
                    {stocks.filter(s => s.sector === sector.name).length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="sidebar-section">
            <h3 className="section-title">
              <i className="fas fa-chart-bar"></i> Quick Stats
            </h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{filteredStocks.length}</div>
                <div className="stat-label">Total Stocks</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  ${filteredStocks.reduce((sum, stock) => sum + (stock.price || 0), 0).toFixed(0)}
                </div>
                <div className="stat-label">Avg Price</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {filteredStocks.filter(s => s.score >= 70).length}
                </div>
                <div className="stat-label">High Score</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {filteredStocks.filter(s => s.rsi < 30).length}
                </div>
                <div className="stat-label">Oversold</div>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {showFilters && (
            <div className="sidebar-section">
              <div className="section-header">
                <h3 className="section-title">
                  <i className="fas fa-sliders-h"></i> Filters
                </h3>
                <button 
                  className="btn btn-sm btn-outline"
                  onClick={resetFilters}
                >
                  <i className="fas fa-redo"></i> Reset
                </button>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">Price Range</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    className="range-input"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    placeholder="Min"
                  />
                  <span className="range-separator">to</span>
                  <input
                    type="number"
                    className="range-input"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    placeholder="Max"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Market Cap</label>
                <div className="range-inputs">
                  <select
                    className="range-input"
                    value={filters.minMarketCap}
                    onChange={(e) => handleFilterChange('minMarketCap', e.target.value)}
                  >
                    <option value="0">Any</option>
                    <option value="1000000000">$1B+</option>
                    <option value="10000000000">$10B+</option>
                    <option value="100000000000">$100B+</option>
                  </select>
                  <span className="range-separator">to</span>
                  <select
                    className="range-input"
                    value={filters.maxMarketCap}
                    onChange={(e) => handleFilterChange('maxMarketCap', e.target.value)}
                  >
                    <option value="1000000000000">$1T</option>
                    <option value="100000000000">$100B</option>
                    <option value="10000000000">$10B</option>
                  </select>
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">P/E Ratio</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    className="range-input"
                    value={filters.minPE}
                    onChange={(e) => handleFilterChange('minPE', e.target.value)}
                    placeholder="Min"
                  />
                  <span className="range-separator">to</span>
                  <input
                    type="number"
                    className="range-input"
                    value={filters.maxPE}
                    onChange={(e) => handleFilterChange('maxPE', e.target.value)}
                    placeholder="Max"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Dividend Yield</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    className="range-input"
                    value={filters.minDividend}
                    onChange={(e) => handleFilterChange('minDividend', e.target.value)}
                    placeholder="Min"
                    step="0.1"
                  />
                  <span className="range-separator">to</span>
                  <input
                    type="number"
                    className="range-input"
                    value={filters.maxDividend}
                    onChange={(e) => handleFilterChange('maxDividend', e.target.value)}
                    placeholder="Max"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">RSI Range</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    className="range-input"
                    value={filters.minRSI}
                    onChange={(e) => handleFilterChange('minRSI', e.target.value)}
                    placeholder="Min"
                  />
                  <span className="range-separator">to</span>
                  <input
                    type="number"
                    className="range-input"
                    value={filters.maxRSI}
                    onChange={(e) => handleFilterChange('maxRSI', e.target.value)}
                    placeholder="Max"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Momentum</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    className="range-input"
                    value={filters.minMomentum}
                    onChange={(e) => handleFilterChange('minMomentum', e.target.value)}
                    placeholder="Min"
                  />
                  <span className="range-separator">to</span>
                  <input
                    type="number"
                    className="range-input"
                    value={filters.maxMomentum}
                    onChange={(e) => handleFilterChange('maxMomentum', e.target.value)}
                    placeholder="Max"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Sort By</label>
                <div className="sort-controls">
                  <select
                    className="sort-select"
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  >
                    <option value="score">Score</option>
                    <option value="price">Price</option>
                    <option value="market_cap">Market Cap</option>
                    <option value="pe_ratio">P/E Ratio</option>
                    <option value="dividend_yield">Dividend Yield</option>
                    <option value="rsi">RSI</option>
                    <option value="momentum">Momentum</option>
                  </select>
                  <button
                    className="sort-order-btn"
                    onClick={() => handleFilterChange('sortOrder', 
                      filters.sortOrder === 'asc' ? 'desc' : 'asc'
                    )}
                  >
                    <i className={`fas fa-sort-amount-${filters.sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="scanner-content">
          {/* Content Header */}
          <div className="content-header">
            <div className="results-info">
              <h2 className="sector-title">
                {sectors.find(s => s.key === selectedSector)?.name || 'All Sectors'}
              </h2>
              <p className="results-count">
                Showing {Math.min(itemsPerPage, currentStocks.length)} of {filteredStocks.length} stocks
              </p>
            </div>
            <div className="content-actions">
              <select
                className="items-per-page"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              >
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
              <button 
                className="btn btn-outline"
                onClick={fetchStocksBySector}
              >
                <i className="fas fa-sync-alt"></i> Refresh
              </button>
            </div>
          </div>

          {/* Stock Grid/Table View */}
          {viewMode === 'grid' ? (
            // Grid View
            <div className="stocks-grid">
              {currentStocks.map((stock) => (
                <div 
                  key={stock.symbol} 
                  className={`stock-card ${selectedStock?.symbol === stock.symbol ? 'selected' : ''}`}
                  onClick={() => handleStockSelect(stock)}
                >
                  {/* Card Header */}
                  <div className="card-header">
                    <div className="stock-symbol">
                      <span className="symbol">{stock.symbol}</span>
                      <span className="sector-tag">{stock.sector}</span>
                    </div>
                    <div 
                      className="score-badge"
                      style={{ backgroundColor: getScoreColor(stock.score) }}
                    >
                      {stock.score.toFixed(0)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="card-body">
                    <h3 className="stock-name">{stock.name}</h3>
                    
                    <div className="stock-price">
                      <span className="price">${stock.price.toFixed(2)}</span>
                      <span className={`change ${stock.momentum >= 0 ? 'positive' : 'negative'}`}>
                        {stock.momentum >= 0 ? '+' : ''}{stock.momentum.toFixed(2)}%
                      </span>
                    </div>

                    <div className="stock-metrics">
                      <div className="metric">
                        <span className="metric-label">Market Cap</span>
                        <span className="metric-value">{formatMarketCap(stock.market_cap)}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">P/E</span>
                        <span className="metric-value">{stock.pe_ratio.toFixed(1)}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Div Yield</span>
                        <span className="metric-value">{stock.dividend_yield.toFixed(2)}%</span>
                      </div>
                    </div>

                    <div className="technical-indicators">
                      <div className="indicator">
                        <span className="indicator-label">RSI</span>
                        <div className="indicator-value">
                          <span className="value">{stock.rsi.toFixed(1)}</span>
                          <div className="indicator-bar">
                            <div 
                              className="bar-fill"
                              style={{
                                width: `${stock.rsi}%`,
                                backgroundColor: getRSIColor(stock.rsi)
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      <div className="indicator">
                        <span className="indicator-label">Beta</span>
                        <span className={`value ${stock.beta > 1.5 ? 'high' : stock.beta < 0.8 ? 'low' : ''}`}>
                          {stock.beta.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="card-footer">
                    <button className="btn btn-sm btn-outline">
                      <i className="fas fa-chart-line"></i> Analyze
                    </button>
                    <button className="btn btn-sm btn-primary">
                      <i className="fas fa-plus"></i> Add to Watchlist
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Table View
            <div className="stocks-table-container">
              <table className="stocks-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Name</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Market Cap</th>
                    <th className="text-right">P/E</th>
                    <th className="text-right">Div Yield</th>
                    <th className="text-right">Beta</th>
                    <th className="text-right">RSI</th>
                    <th className="text-right">Momentum</th>
                    <th className="text-right">Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentStocks.map((stock) => (
                    <tr 
                      key={stock.symbol}
                      className={selectedStock?.symbol === stock.symbol ? 'selected' : ''}
                      onClick={() => handleStockSelect(stock)}
                    >
                      <td>
                        <div className="symbol-cell">
                          <span className="symbol">{stock.symbol}</span>
                          <span className="sector">{stock.sector}</span>
                        </div>
                      </td>
                      <td className="name-cell">
                        <div className="stock-name">{stock.name}</div>
                      </td>
                      <td className="text-right">
                        <div className="price-cell">
                          <span className="price">${stock.price.toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="text-right">
                        {formatMarketCap(stock.market_cap)}
                      </td>
                      <td className="text-right">
                        {stock.pe_ratio.toFixed(1)}
                      </td>
                      <td className="text-right">
                        {stock.dividend_yield.toFixed(2)}%
                      </td>
                      <td className="text-right">
                        <span className={`beta ${stock.beta > 1.5 ? 'high' : stock.beta < 0.8 ? 'low' : ''}`}>
                          {stock.beta.toFixed(2)}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="rsi-cell">
                          <span className="rsi-value">{stock.rsi.toFixed(1)}</span>
                          <div className="rsi-bar">
                            <div 
                              className="bar-fill"
                              style={{
                                width: `${stock.rsi}%`,
                                backgroundColor: getRSIColor(stock.rsi)
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="text-right">
                        <span className={`momentum ${stock.momentum >= 0 ? 'positive' : 'negative'}`}>
                          {stock.momentum >= 0 ? '+' : ''}{stock.momentum.toFixed(2)}%
                        </span>
                      </td>
                      <td className="text-right">
                        <div 
                          className="score-circle"
                          style={{ backgroundColor: getScoreColor(stock.score) }}
                        >
                          {stock.score.toFixed(0)}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" title="Analyze">
                            <i className="fas fa-chart-line"></i>
                          </button>
                          <button className="btn-icon" title="Add to Watchlist">
                            <i className="fas fa-plus"></i>
                          </button>
                          <button className="btn-icon" title="Risk Check">
                            <i className="fas fa-shield-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Selected Stock Details Panel */}
          {selectedStock && (
            <div className="stock-details-panel">
              <div className="panel-header">
                <h3 className="panel-title">
                  <i className="fas fa-info-circle"></i> {selectedStock.symbol} Details
                </h3>
                <button 
                  className="btn-icon"
                  onClick={() => setSelectedStock(null)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="panel-content">
                <div className="detail-section">
                  <h4 className="section-title">Fundamentals</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Company</span>
                      <span className="detail-value">{selectedStock.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Sector</span>
                      <span className="detail-value">{selectedStock.sector}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Current Price</span>
                      <span className="detail-value">${selectedStock.price.toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Market Cap</span>
                      <span className="detail-value">{formatMarketCap(selectedStock.market_cap)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">P/E Ratio</span>
                      <span className="detail-value">{selectedStock.pe_ratio.toFixed(1)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Dividend Yield</span>
                      <span className="detail-value">{selectedStock.dividend_yield.toFixed(2)}%</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Beta</span>
                      <span className="detail-value">{selectedStock.beta.toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Volume</span>
                      <span className="detail-value">
                        {(selectedStock.volume / 1000000).toFixed(2)}M
                      </span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4 className="section-title">Technical Analysis</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">RSI (14)</span>
                      <div className="detail-value-with-indicator">
                        <span>{selectedStock.rsi.toFixed(1)}</span>
                        <div className={`rsi-status ${selectedStock.rsi > 70 ? 'overbought' : selectedStock.rsi < 30 ? 'oversold' : 'neutral'}`}>
                          {selectedStock.rsi > 70 ? 'Overbought' : selectedStock.rsi < 30 ? 'Oversold' : 'Neutral'}
                        </div>
                      </div>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Momentum (20D)</span>
                      <span className={`detail-value ${selectedStock.momentum >= 0 ? 'positive' : 'negative'}`}>
                        {selectedStock.momentum >= 0 ? '+' : ''}{selectedStock.momentum.toFixed(2)}%
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">SMA 50</span>
                      <span className="detail-value">${selectedStock.sma_50?.toFixed(2) || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">SMA 200</span>
                      <span className="detail-value">${selectedStock.sma_200?.toFixed(2) || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Volatility</span>
                      <span className="detail-value">{selectedStock.volatility?.toFixed(1) || 'N/A'}%</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Overall Score</span>
                      <div className="detail-value-with-indicator">
                        <span>{selectedStock.score.toFixed(0)}/100</span>
                        <div 
                          className="score-bar"
                          style={{ backgroundColor: getScoreColor(selectedStock.score) }}
                        >
                          <div 
                            className="score-fill"
                            style={{ width: `${selectedStock.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4 className="section-title">Quick Actions</h4>
                  <div className="action-buttons-group">
                    <button className="btn btn-primary">
                      <i className="fas fa-chart-line"></i> Detailed Analysis
                    </button>
                    <button className="btn btn-success">
                      <i className="fas fa-calculator"></i> Risk Check
                    </button>
                    <button className="btn btn-warning">
                      <i className="fas fa-bell"></i> Set Alert
                    </button>
                    <button className="btn btn-outline">
                      <i className="fas fa-file-export"></i> Export Data
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>
              
              <div className="page-numbers">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  if (pageNum < 1 || pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                {totalPages > 5 && (
                  <>
                    {currentPage < totalPages - 2 && <span className="page-dots">...</span>}
                    <button
                      className={`page-number ${currentPage === totalPages ? 'active' : ''}`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockScanner;